// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { getApiBase, fetchJson } from '../../apiBase';
import { normalizePhone } from '../../utils/phone';
import RoleFilter from './components/RoleFilter';
import ClassSelector from './components/ClassSelector';
import SearchInput from './components/SearchInput';
import ThreadList from './components/ThreadList';
import ChatMessages from './components/ChatMessages';
import MessageComposer from './components/MessageComposer';
import ChatHeader from './components/ChatHeader';

function getThreadPreviewText(item) {
  const messageType = String(item?.message_type || '').trim();
  const meta = item?.meta || {};

  if (messageType === 'reaction') {
    const emoji = String(meta?.message?.reaction?.emoji || '').trim();
    return emoji ? `${emoji} Reaksiyon` : 'Reaksiyon';
  }

  if (messageType === 'button') {
    const button = meta?.message?.button || {};
    const text = String(button.text || '').trim();
    const payload = String(button.payload || '').trim();
    return text || payload || 'Buton yanıtı';
  }

  if (messageType === 'interactive') {
    const interactive = meta?.message?.interactive || {};
    const buttonReply = interactive.button_reply || {};
    const listReply = interactive.list_reply || {};
    const title = String(buttonReply.title || listReply.title || '').trim();
    const description = String(listReply.description || '').trim();
    const fallbackId = String(buttonReply.id || listReply.id || '').trim();
    return title || description || fallbackId || 'Buton yanıtı';
  }

  return String(item?.body || '');
}

function normalizeThreadDirection(value) {
  const direction = String(value || '').trim().toLowerCase();

  if (['in', 'incoming', 'inbound', 'received', 'receive', 'from_user', 'user'].includes(direction)) {
    return 'in';
  }

  if (['out', 'outgoing', 'outbound', 'sent', 'send', 'to_user', 'agent', 'business'].includes(direction)) {
    return 'out';
  }

  return '';
}

function compareThreadsByTimestampDesc(a, b) {
  if (a.timestamp && b.timestamp) return a.timestamp.localeCompare(b.timestamp) * -1;
  if (a.timestamp) return -1;
  if (b.timestamp) return 1;
  return 0;
}

function api() {
  const base = `${getApiBase()}/admin`;
  return {
    // Request a larger window so per-class filtering in UI has full data.
    threads: (school, role) => {
      let q = `limit=500&offset=0`;
      if (role) q += `&role=${encodeURIComponent(role)}`;
      return `${base}/messages/threads/${encodeURIComponent(school)}?${q}`;
    },
    history: (school, phone) => `${base}/messages/history/${encodeURIComponent(school)}?phone=${encodeURIComponent(phone)}`,
    send: () => `${base}/messages/send`,
    classes: (school) => `${base}/classes/${encodeURIComponent(school)}`,
    parentsByClass: (school, classId) => `${base}/parents/${encodeURIComponent(school)}/by-class/${encodeURIComponent(classId)}`,
    parentsPreviewByClass: (school, classId) => `${base}/messages/parents-preview/${encodeURIComponent(school)}/by-class/${encodeURIComponent(classId)}`,
    parentsAll: (school) => `${base}/parents/${encodeURIComponent(school)}`,
  };
}

export default function MessagesPage({ schoolId, onError }) {
  // WhatsApp-inspired palettes (light + dark). Dark matches provided screenshot.
  const prefersDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const useDarkWhatsApp = prefersDark; // could be replaced with user setting / theme context

  const LIGHT = {
    CHAT_BG: '#EFEAE2',
    OUT_BG: '#D9FDD3',
    OUT_BORDER: '#B7E6A1',
    IN_BG: '#FFFFFF',
    IN_BORDER: '#E0E0E0',
    TEXT: '#111B21',
    TIMESTAMP: '#667781',
  };
  const DARK = {
    CHAT_BG: '#0B141A', // base dark
    OUT_BG: '#005C4B',
    OUT_BORDER: '#025144',
    IN_BG: '#202C33',
    IN_BORDER: '#2A3942',
    TEXT: '#E9EDEF',
    TIMESTAMP: '#8696A0',
  };

  const PAL = useDarkWhatsApp ? DARK : LIGHT;
  const roleLabels = {
    parent: 'Veli',
    teacher: 'Öğretmen',
    admin: 'Admin',
    unknown: 'Bilinmeyen',
  };
  const roleOptions = [
    { value: 'parents', label: 'Veliler' },
    { value: 'teachers', label: 'Öğretmenler' },
    { value: 'admins', label: 'Adminler' },
    { value: 'unknown', label: 'Bilinmeyen' },
    { value: 'all', label: 'Hepsi' },
  ];

  const [threads, setThreads] = useState([]); // raw threads from API
  const [roleFilter, setRoleFilter] = useState('parents'); // parents | teachers | admins | unknown | all
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const PAGE_SIZE = 50; // Load last 50 messages initially for faster UX
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loading, setLoading] = useState(false); // message history loading
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [drafts, setDrafts] = useState(new Map()); // phone -> draft text
  const draftsRef = useRef(drafts);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [parentsMap, setParentsMap] = useState(new Map()); // phone -> full name
  const [parentList, setParentList] = useState([]); // full list for the class [{phone,name,surname}]
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isParentMode = roleFilter === 'parents';

  // Resizable panes state
  const [leftPct, setLeftPct] = useState(34);
  const draggingRef = useRef(false);
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const listScrollRef = useRef(null);
  // Use local public asset (served from /public/admin)
  const chatBgUrl = `${process.env.PUBLIC_URL || ''}/admin/wa_bg.png`;

  // Pull-to-refresh state (mobile list)
  const [pulling, setPulling] = useState(false);
  const [pullDist, setPullDist] = useState(0);
  const pullStartYRef = useRef(0);

  const apis = useMemo(() => api(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'chat'

  const refreshPreviewForSelectedClass = useCallback(async () => {
    if (!schoolId || !selectedClass) return;
    setLoadingParents(true);
    try {
      // Find class object to get ID
      const classObj = classes.find(c => (typeof c === 'object' ? c.name : c) === selectedClass);
      const classId = typeof classObj === 'object' ? classObj.id : null;
      if (!classId) {
        setLoadingParents(false);
        return;
      }
      const res = await fetchJson(apis.parentsPreviewByClass(schoolId, classId));
      if (res.ok) {
        const payload = await res.json();
        const plist = Array.isArray(payload?.items) ? payload.items : [];
        setParentList(plist);
        const map = new Map();
        (plist || []).forEach((p) => {
          if (p.phone) map.set(String(p.phone), `${p.name || ''} ${p.surname || ''}`.trim());
        });
        setParentsMap(map);
      }
    } catch {
      // swallow UI errors
    } finally {
      setLoadingParents(false);
    }
  }, [apis, schoolId, selectedClass, classes]);

  const loadThreads = useCallback(async () => {
    if (!schoolId) return;
    setLoadingThreads(true);
    try {
      const roleParam = {
        parents: 'parent',
        teachers: 'teacher',
        admins: 'admin',
        unknown: 'unknown',
        all: '',
      }[roleFilter] || '';
      const res = await fetchJson(apis.threads(schoolId, roleParam || undefined));
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      setThreads(items.map((t) => ({ ...t, phone: String(t.phone || ''), role: t.role || (t.phone === '_unknown' ? 'unknown' : ''), name: t.name || '' })));
    } catch (e) {
      onError && onError('Mesaj konuları yüklenemedi');
    } finally {
      setLoadingThreads(false);
    }
  }, [apis, schoolId, onError, roleFilter]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads, roleFilter]);

  const loadHistory = useCallback(
    async (phone) => {
      setLoading(true);
      try {
        const phoneNorm = phone === '_unknown' ? '_unknown' : normalizePhone(phone);
        // Single API call: load last PAGE_SIZE messages (most recent)
        // Backend returns them in chronological order along with total count
        const res = await fetchJson(apis.history(schoolId, phoneNorm) + `&limit=${PAGE_SIZE * 2}&offset=0`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        const total = Number(data.total || 0);
        const allItems = (data.items || []).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
        
        // Take last PAGE_SIZE messages from the result
        const items = allItems.slice(-PAGE_SIZE);
        const actualOffset = Math.max(0, total - items.length);
        
        setHistory(items);
        setHistoryOffset(actualOffset);
        
        // Scroll to bottom to show most recent messages
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            setIsAtBottom(true);
            setShowScrollToLatest(false);
          }
        });
      } catch (e) {
        onError && onError('Mesaj geçmişi yüklenemedi');
      } finally {
        setLoading(false);
      }
    },
    [apis, schoolId, onError]
  );

  const loadOlder = useCallback(async () => {
    if (!selected || loadingOlder) return;
    if (historyOffset <= 0) return; // No more messages to load
    try {
      setLoadingOlder(true);
      const newOffset = Math.max(0, historyOffset - PAGE_SIZE);
      const take = historyOffset - newOffset;
      const prevScrollHeight = scrollRef.current ? scrollRef.current.scrollHeight : 0;
      
      const res = await fetchJson(
        apis.history(schoolId, selected.phone === '_unknown' ? '_unknown' : normalizePhone(selected.phone)) + 
        `&limit=${take}&offset=${newOffset}`
      );
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const items = (data.items || []).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      
      // Prepend older messages to history
      setHistory((prev) => [...items, ...prev]);
      setHistoryOffset(newOffset);
      
      // Maintain scroll position by adjusting for new content height
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          const newH = scrollRef.current.scrollHeight;
          const delta = newH - prevScrollHeight;
          scrollRef.current.scrollTop = delta + (scrollRef.current.scrollTop || 0);
        }
      });
    } catch (e) {
      onError && onError('Önceki mesajlar yüklenemedi');
    } finally {
      setLoadingOlder(false);
    }
  }, [apis, schoolId, selected, historyOffset, loadingOlder, onError]);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  const handleSend = async () => {
    if (!selected || !text.trim()) return;
    if (selected.phone === '_unknown') return; // cannot reply to unknown contact id
    setSending(true);
    const nowIso = new Date().toISOString();
    const tempId = `temp-${Date.now()}`;
    const optimistic = { id: tempId, body: text, direction: 'out', timestamp: nowIso, status: 'pending' };
    setHistory((prev) => [...prev, optimistic]);
    setDrafts((prev) => {
      const m = new Map(prev);
      m.set(String(selected.phone), '');
      return m;
    });
    try {
      const res = await fetchJson(apis.send(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizePhone(selected.phone), body: text, school: schoolId }),
      });
      if (!res.ok) throw new Error('failed');
      setText('');
      setHistory((prev) => prev.map((m) => (m.id === tempId ? { ...m, status: 'sent' } : m)));
      await refreshPreviewForSelectedClass();
      await loadHistory(selected.phone);
      await loadThreads();
    } catch (e) {
      onError && onError('Mesaj gönderilemedi');
      setHistory((prev) => prev.map((m) => (m.id === tempId ? { ...m, status: 'failed' } : m)));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!selected) return;
    const d = draftsRef.current.get(String(selected.phone)) || '';
    setText(d);
    loadHistory(selected.phone);
    if (isMobile) setMobileView('chat');
  }, [selected, loadHistory, isMobile]);

  useEffect(() => {
    const run = async () => {
      setLoadingClasses(true);
      try {
        const res = await fetchJson(apis.classes(schoolId));
        if (res.ok) {
          const arr = await res.json();
          const classList = Array.isArray(arr) ? arr : [];
          setClasses(classList);
        }
      } catch {}
      finally {
        setLoadingClasses(false);
      }
    };
    if (schoolId) run();
  }, [schoolId, apis]);

  useEffect(() => {
    setParentsMap(new Map());
    setParentList([]);
    if (!selectedClass) return;
    refreshPreviewForSelectedClass();
  }, [selectedClass, refreshPreviewForSelectedClass]);

  const parentPreviewMap = useMemo(() => {
    const map = new Map();
    parentList.forEach((p) => {
      if (p && p.phone) {
        map.set(String(p.phone), p);
      }
    });
    return map;
  }, [parentList]);

  const parentsInClass = useMemo(() => {
    const set = new Set();
    parentList.forEach((p) => {
      if (p && p.phone) set.add(String(p.phone));
    });
    return set;
  }, [parentList]);

  const filteredThreads = useMemo(() => {
    let base = threads;
    const targetRole = {
      parents: 'parent',
      teachers: 'teacher',
      admins: 'admin',
      unknown: 'unknown',
      all: '',
    }[roleFilter] || '';
    if (targetRole) {
      base = base.filter((t) => (t.role || (t.phone === '_unknown' ? 'unknown' : '')) === targetRole);
    }
    if (roleFilter === 'parents' && !selectedClass) {
      base = [];
    }
    if (roleFilter === 'parents' && selectedClass) {
      base = base.filter((t) => parentsInClass.has(String(t.phone || '')));
    }
    const enriched = base.map((t) => {
      const phone = String(t.phone || '');
      const preview = parentPreviewMap.get(phone);
      const nameFromPreview = preview ? `${preview.name || ''} ${preview.surname || ''}`.trim() : '';
      const message_type = (preview && preview.message_type) || t.message_type || '';
      const meta = (preview && preview.meta) || t.meta || {};
      const body = getThreadPreviewText({ ...(preview || {}), ...t, message_type, meta });
      const timestamp = (preview && preview.timestamp) || t.timestamp || '';
      const totalMessages = typeof (preview && preview.total_messages) === 'number' ? preview.total_messages : t.total_messages;
      const status = (preview && preview.status) || t.status || '';
      const role = t.role || (phone === '_unknown' ? 'unknown' : '');
      const name = nameFromPreview || parentsMap.get(phone) || t.name || (phone === '_unknown' ? 'Bilinmeyen' : '');
      const direction = normalizeThreadDirection(
        (preview && (preview.direction || preview.last_direction || preview.message_direction)) ||
          t.direction ||
          t.last_direction ||
          t.message_direction ||
          meta?.direction ||
          meta?.last_direction
      );
      return {
        ...t,
        phone,
        role,
        name: name || phone || 'Bilinmeyen',
        body,
        timestamp,
        total_messages: totalMessages,
        status,
        direction,
        message_type,
        meta,
      };
    });
    const q = debouncedSearch.trim().toLowerCase();
    const filtered = q
      ? enriched.filter(
          (t) =>
            String(t.phone).includes(q) ||
            String(t.name || '').toLowerCase().includes(q) ||
            String(t.body || '').toLowerCase().includes(q)
        )
      : enriched;
    filtered.sort((a, b) => {
      if (roleFilter === 'all') {
        const aIncoming = a.direction === 'in' ? 1 : 0;
        const bIncoming = b.direction === 'in' ? 1 : 0;

        if (aIncoming !== bIncoming) return bIncoming - aIncoming;
      }

      const timestampCompare = compareThreadsByTimestampDesc(a, b);
      if (timestampCompare !== 0) return timestampCompare;

      return String(a.name || '').localeCompare(String(b.name || ''), 'tr-TR');
    });
    return filtered;
  }, [threads, roleFilter, selectedClass, parentsInClass, parentPreviewMap, debouncedSearch, parentsMap]);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (isAtBottom) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setShowScrollToLatest(false);
    } else {
      setShowScrollToLatest(true);
    }
  }, [history, isAtBottom]);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(20, Math.min(60, (x / rect.width) * 100));
      setLeftPct(pct);
    };
    const onUp = () => {
      draggingRef.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const formatTimestamp = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', hour12: false });
    } catch {
      return ts;
    }
  };

  const handleRoleChange = useCallback((v) => {
    setRoleFilter(v);
    setSelected(null);
    setSearch('');
    if (v !== 'parents') setSelectedClass('');
  }, []);

  const handleClassChange = useCallback((v) => {
    setSelectedClass(v);
    setSearch('');
    setSelected(null);
  }, []);

  const handleThreadSelect = useCallback(
    (t) => {
      setSelected(t);
      if (isMobile) setMobileView('chat');
    },
    [isMobile]
  );

  const handleDraftChange = useCallback((phone, value) => {
    setDrafts((prev) => {
      const m = new Map(prev);
      m.set(String(phone), value);
      return m;
    });
  }, []);

  const selectedInfo = selected
    ? `${selected.phone} • ${(parentsMap.get(String(selected.phone)) || selected.name || 'Bilinmeyen')}${
        selected.role ? ' • ' + (roleLabels[selected.role] || '') : ''
      }`
    : '';

  if (isMobile) {
    return (
      <Box
        ref={containerRef}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: { xs: '70vh', md: 540 }, overflow: 'hidden', backgroundColor: PAL.CHAT_BG, borderRadius: 2 }}
      >
        {mobileView === 'list' && (
          <Paper sx={{ p: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ChatHeader title="WhatsApp Mesajları" palette={PAL} sticky variant="mobile" />
            <Box sx={{ px: 2, pt: 1 }}>
              <RoleFilter value={roleFilter} options={roleOptions} onChange={handleRoleChange} fullWidth />
            </Box>
            {pulling && (
              <Box sx={{ px: 2, pt: 1 }}>
                <LinearProgress variant="determinate" value={Math.min(100, (pullDist / 80) * 100)} sx={{ height: 6, borderRadius: 3 }} />
              </Box>
            )}
            <Box sx={{ px: 2, pt: 2 }}>
              <ClassSelector
                classes={classes}
                value={isParentMode ? selectedClass || null : null}
                onChange={handleClassChange}
                loading={loadingClasses}
                disabled={!isParentMode}
                minWidth={200}
              />
            </Box>
            <Box sx={{ px: 2, pt: 1 }}>
              <SearchInput value={search} onChange={setSearch} disabled={isParentMode && !selectedClass} />
            </Box>
            <Divider />
            <Box
              ref={listScrollRef}
              sx={{ flex: 1, overflow: 'auto', mt: 1 }}
              onTouchStart={(e) => {
                const el = listScrollRef.current;
                if (!el) return;
                if (el.scrollTop <= 0) {
                  pullStartYRef.current = e.touches[0].clientY;
                  setPulling(true);
                  setPullDist(0);
                } else {
                  setPulling(false);
                }
              }}
              onTouchMove={(e) => {
                if (!pulling) return;
                const dy = e.touches[0].clientY - pullStartYRef.current;
                if (dy > 0) {
                  e.preventDefault();
                  setPullDist(Math.min(80, dy));
                } else {
                  setPulling(false);
                  setPullDist(0);
                }
              }}
              onTouchEnd={async () => {
                if (pulling && pullDist > 60) {
                  await refreshPreviewForSelectedClass();
                }
                setPulling(false);
                setPullDist(0);
              }}
            >
              <ThreadList
                threads={filteredThreads}
                loadingThreads={loadingThreads}
                loadingParents={loadingParents}
                isParentMode={isParentMode}
                selectedClass={selectedClass}
                parentsMap={parentsMap}
                selected={selected}
                onSelect={handleThreadSelect}
                roleLabels={roleLabels}
                useDarkWhatsApp={useDarkWhatsApp}
                variant="mobile"
                missingClassMessage="Önce bir sınıf seçin"
              />
            </Box>
          </Paper>
        )}

        {mobileView === 'chat' && (
          <Paper sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: PAL.CHAT_BG, boxShadow: 'none', position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${chatBgUrl})`, backgroundRepeat: 'repeat', backgroundSize: 'auto', opacity: 0.18, pointerEvents: 'none', zIndex: 0 }} />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <ChatHeader title="WhatsApp Mesajları" palette={PAL} sticky variant="mobile" onBack={() => setMobileView('list')} info={selectedInfo} />
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ChatMessages
                  selected={selected}
                  loading={loading}
                  history={history}
                  formatTimestamp={formatTimestamp}
                  palette={PAL}
                  scrollRef={scrollRef}
                  onLoadOlder={loadOlder}
                  onIsAtBottomChange={setIsAtBottom}
                  onShowScrollToLatestChange={setShowScrollToLatest}
                  showScrollToLatest={showScrollToLatest}
                  bubbleMaxWidth="88%"
                  bubblePadding={{ px: 1.5, py: 1 }}
                  skeletonWidth={200}
                  withShadow={false}
                />
              </Box>
              <Divider />
              <MessageComposer
                text={text}
                onChangeText={setText}
                onDraftChange={(v) => selected && handleDraftChange(selected.phone, v)}
                onSend={handleSend}
                disabled={!selected}
                disableSend={!selected || selected?.phone === '_unknown' || sending || !text.trim()}
                sending={sending}
                palette={PAL}
                useDarkWhatsApp={useDarkWhatsApp}
                placeholder={selected ? 'Mesaj yazın' : 'Önce veli seçin'}
              />
            </Box>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'row', gap: 0, height: 540, minWidth: 650, overflow: 'hidden', backgroundColor: PAL.CHAT_BG }}>
      <Paper sx={{ p: 2, height: '100%', width: `${leftPct}%`, minWidth: 280, overflow: 'hidden', borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            WhatsApp Mesajları
          </Typography>
          <RoleFilter value={roleFilter} options={roleOptions} onChange={handleRoleChange} color="primary" />
          <ClassSelector
            classes={classes}
            value={isParentMode ? selectedClass || null : null}
            onChange={handleClassChange}
            loading={loadingClasses}
            disabled={!isParentMode}
          />
          <SearchInput value={search} onChange={setSearch} disabled={isParentMode && !selectedClass} />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflow: 'auto', mt: 1 }}>
          <ThreadList
            threads={filteredThreads}
            loadingThreads={loadingThreads}
            loadingParents={loadingParents}
            isParentMode={isParentMode}
            selectedClass={selectedClass}
            parentsMap={parentsMap}
            selected={selected}
            onSelect={handleThreadSelect}
            roleLabels={roleLabels}
            useDarkWhatsApp={useDarkWhatsApp}
          />
        </Box>
      </Paper>

      <Box
        onMouseDown={() => {
          draggingRef.current = true;
        }}
        sx={{ width: 6, cursor: 'col-resize', '&:hover': { bgcolor: 'action.hover' }, backgroundColor: 'background.default' }}
        title="Kaydırarak boyutlandırın"
        role="separator"
        aria-orientation="vertical"
      />

      <Paper sx={{ p: 2, height: '100%', flex: 1, minWidth: 360, display: 'flex', flexDirection: 'column', gap: 2, backgroundColor: PAL.CHAT_BG, boxShadow: 'none', position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${chatBgUrl})`, backgroundRepeat: 'repeat', backgroundSize: 'auto', opacity: 0.18, pointerEvents: 'none' }} />
        <ChatHeader title="WhatsApp Mesajları" palette={PAL} info={selectedInfo} sticky={false} />
        <Divider />
        <ChatMessages
          selected={selected}
          loading={loading}
          history={history}
          formatTimestamp={formatTimestamp}
          palette={PAL}
          scrollRef={scrollRef}
          onLoadOlder={loadOlder}
          onIsAtBottomChange={setIsAtBottom}
          onShowScrollToLatestChange={setShowScrollToLatest}
          showScrollToLatest={showScrollToLatest}
        />
        <Divider />
        <MessageComposer
          text={text}
          onChangeText={setText}
          onDraftChange={(v) => selected && handleDraftChange(selected.phone, v)}
          onSend={handleSend}
          disabled={!selected}
          disableSend={!selected || selected?.phone === '_unknown' || sending || !text.trim()}
          sending={sending}
          palette={PAL}
          useDarkWhatsApp={useDarkWhatsApp}
          placeholder={selected ? 'Mesaj yazın' : 'Önce veli seçin'}
        />
      </Paper>
    </Box>
  );
}
